import { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Layout,
  theme,
  Row,
  Col,
  Card,
  Typography,
  Alert,
  message,
  Select,
  Dropdown,
  Button,
} from "antd";
import {
  SafetyCertificateOutlined,
  IdcardOutlined,
  FormOutlined,
  FileProtectOutlined,
  ReloadOutlined,
  WarningOutlined,
  FileExcelOutlined,
  MailOutlined,
  WalletOutlined,
  BankOutlined,
  MobileOutlined,
  ClockCircleOutlined,
  StopOutlined,
  UserDeleteOutlined,
  TeamOutlined,
  FileSearchOutlined,
  ToolOutlined,
  DollarOutlined,
  DownOutlined,
} from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";
import type { MenuProps } from "antd";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { NonCompliantMembersModal } from "../../components/ModalForm";

import { AuthContext } from "../../context/AuthContext";

import {
  getDocumentComplianceApi,
  ComplianceCard,
  ComplianceRow,
  DocumentComplianceData,
} from "../../services/Compliance/ComplianceApis";
import {
  ComplianceCardKey,
  COMPLIANCE_CARD_META,
  getPolicyPremiumFloor,
  getOutstandingPremium,
  getMonthsBehindForMember,
  formatMonthsBehind,
  getDebiCheckIssueForMember,
  getGeneratedNotSentDocuments,
  hasValidDocumentLink,
  isDeductionBelowPremium,
  parsePremium,
} from "../../utils/documentCompliance";
import { getCompanyMembersApi } from "../../services/Member/MemberApis";
import {
  isValidSACellphone,
  getCellphoneIssueDetail,
} from "../../utils/systemValidation";
import {
  formatPendingLapseSummary,
  usePendingLapseRuns,
} from "../../hooks/usePendingLapseRuns";
import { LapseRun } from "../../services/Admin/LapseRunApis";
import { subscribeComplianceInvalidate } from "../../utils/complianceInvalidate";

const { Content } = Layout;
const { Text } = Typography;

const buildInvalidCellphoneCard = (members: any[]): ComplianceCard => {
  const meta = COMPLIANCE_CARD_META.InvalidCellphone;
  const rows: ComplianceRow[] = (Array.isArray(members) ? members : [])
    .filter((member) => !isValidSACellphone(member?.Cellphone))
    .map((member) => ({
      id: member.id,
      IdNumber: member.IdNumber ?? "",
      PolicyNumber: member?.Policy?.PolicyNumber ?? "",
      Name: member.Name ?? "",
      Surname: member.Surname ?? "",
      ProductID: member?.Policy?.ProductID ?? member?.ProductID ?? null,
      ProductName:
        member?.Policy?.Product?.Name ??
        member?.Policy?.Product?.ProductName ??
        member?.ProductName ??
        "",
      AgeDays: null,
      Detail: getCellphoneIssueDetail(member?.Cellphone),
      MissingDocuments: [],
      MemberID: member.id,
      ViewPath: `/members/${member.id}`,
    }));

  return {
    Key: "InvalidCellphone",
    Label: meta.Label,
    Description: meta.Description,
    MissingCount: rows.length,
    Members: rows,
  };
};

const buildDeductionBelowPremiumCard = (members: any[]): ComplianceCard => {
  const meta = COMPLIANCE_CARD_META.DeductionBelowPremium;
  const rows: ComplianceRow[] = (Array.isArray(members) ? members : [])
    .filter((member) => isDeductionBelowPremium(member))
    .map((member) => {
      const floor = getPolicyPremiumFloor(member);
      const deduction = parsePremium(member?.TotalPremium);
      return {
        id: member.id,
        IdNumber: member.IdNumber ?? "",
        PolicyNumber: member?.Policy?.PolicyNumber ?? "",
        Name: member.Name ?? "",
        Surname: member.Surname ?? "",
        ProductID: member?.Policy?.ProductID ?? member?.ProductID ?? null,
        ProductName:
          member?.Policy?.Product?.Name ??
          member?.Policy?.Product?.ProductName ??
          member?.ProductName ??
          "",
        AgeDays: null,
        Detail: `Total deduction R${deduction.toFixed(2)} is below policy premium R${floor.toFixed(2)}`,
        MissingDocuments: [],
        MemberID: member.id,
        ViewPath: `/members/${member.id}`,
      };
    });

  return {
    Key: "DeductionBelowPremium",
    Label: meta.Label,
    Description: meta.Description,
    MissingCount: rows.length,
    Members: rows,
  };
};

/** Enrich API LapseRisk rows with months-behind from loaded members. */
const enrichLapseRiskCard = (
  card: ComplianceCard,
  members: any[]
): ComplianceCard => {
  const byId = new Map<string | number, any>();
  (Array.isArray(members) ? members : []).forEach((m) => {
    if (m?.id != null) byId.set(m.id, m);
  });

  const Members = (card.Members ?? []).map((row) => {
    const member =
      (row.MemberID != null ? byId.get(row.MemberID) : undefined) ??
      byId.get(row.id);
    if (!member) return row;

    const outstanding = getOutstandingPremium(member);
    const monthsBehind = getMonthsBehindForMember(member);
    const absOutstanding = Math.abs(Math.min(outstanding, 0));
    const detailParts = [
      absOutstanding > 0 ? `Outstanding R${absOutstanding.toFixed(2)}` : null,
      monthsBehind != null && monthsBehind > 0
        ? `${formatMonthsBehind(monthsBehind)} behind`
        : null,
      row.Detail && !String(row.Detail).includes("behind")
        ? row.Detail
        : null,
    ].filter(Boolean);

    return {
      ...row,
      MonthsBehind: monthsBehind,
      Detail: detailParts.length
        ? detailParts.join(" · ")
        : row.Detail || formatMonthsBehind(monthsBehind),
    };
  });

  return { ...card, Members };
};

/** Enrich DebiCheckIncomplete rows with severity + clearer Detail from member payments. */
const enrichDebiCheckIncompleteCard = (
  card: ComplianceCard,
  members: any[]
): ComplianceCard => {
  const byId = new Map<string | number, any>();
  (Array.isArray(members) ? members : []).forEach((m) => {
    if (m?.id != null) byId.set(m.id, m);
  });

  const Members = (card.Members ?? []).map((row) => {
    const member =
      (row.MemberID != null ? byId.get(row.MemberID) : undefined) ??
      byId.get(row.id);
    if (!member) return row;

    const issue = getDebiCheckIssueForMember(member);
    if (!issue) return row;

    return {
      ...row,
      Severity: issue.severity,
      Detail: issue.detail,
    };
  });

  Members.sort((a, b) => {
    const rank = (s?: string | null) =>
      s === "error" ? 0 : s === "pending" ? 1 : 2;
    return rank(a.Severity) - rank(b.Severity);
  });

  return { ...card, Members };
};

/** Patch GeneratedNotSent rows: drop false positives via isDocumentSent; enrich Detail. */
const enrichGeneratedNotSentCard = (
  card: ComplianceCard,
  members: any[]
): ComplianceCard => {
  const byId = new Map<string | number, any>();
  (Array.isArray(members) ? members : []).forEach((m) => {
    if (m?.id != null) byId.set(m.id, m);
  });

  const Members = (card.Members ?? [])
    .map((row) => {
      const member =
        (row.MemberID != null ? byId.get(row.MemberID) : undefined) ??
        byId.get(row.id);
      if (!member) return row;

      const docs = Array.isArray(member.PersonalDocument)
        ? member.PersonalDocument
        : Array.isArray(member.Document)
          ? member.Document
          : [];
      const unsent = getGeneratedNotSentDocuments(docs);
      if (docs.length > 0 && unsent.length === 0) {
        return null; // all generated docs look sent on FE — drop false positive
      }

      const evidence = unsent.slice(0, 2).map((d: any) => {
        const sentFlag = d?.DocumentSent;
        const linkOk = hasValidDocumentLink(d);
        return `${d?.Name || "doc"} (DocumentSent=${String(sentFlag)} · Link=${linkOk ? "yes" : "no"})`;
      });

      return {
        ...row,
        Detail:
          evidence.length > 0
            ? evidence.join("; ")
            : row.Detail || "Generated document not marked sent",
      };
    })
    .filter(Boolean) as ComplianceRow[];

  return {
    ...card,
    Members,
    MissingCount: Members.length,
  };
};

const buildPendingLapseCard = (runs: LapseRun[]): ComplianceCard => {
  const meta = COMPLIANCE_CARD_META.PendingLapse;
  const rows: ComplianceRow[] = (Array.isArray(runs) ? runs : []).map((run) => {
    const summary = formatPendingLapseSummary(run);
    return {
      id: run.id,
      IdNumber: "",
      PolicyNumber: "",
      Name: summary.title,
      Surname: "",
      ProductID: null,
      ProductName: run.OwningGroup?.GroupName ?? "",
      AgeDays: null,
      Detail: `Awaiting approval — ${summary.description}`,
      MissingDocuments: [],
      MemberID: null,
      ViewPath: `/maintenance/${run.id}`,
    };
  });

  return {
    Key: "PendingLapse",
    Label: meta.Label,
    Description: meta.Description,
    MissingCount: rows.length,
    Members: rows,
  };
};

type CardTone = "green" | "red" | "yellow" | "grey";

const TONE_STYLES: Record<
  CardTone,
  {
    background: string;
    border: string;
    accent: string;
    iconColor: string;
    statusColor: string;
    countColor: string;
    shadow: string;
  }
> = {
  green: {
    background: "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)",
    border: "1px solid #95de64",
    accent: "#52c41a",
    iconColor: "#389e0d",
    statusColor: "#389e0d",
    countColor: "#237804",
    shadow: "0 4px 12px rgba(0,0,0,0.06)",
  },
  yellow: {
    background: "linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)",
    border: "1px solid #fa8c16",
    accent: "#fa8c16",
    iconColor: "#d46b08",
    statusColor: "#ad4e00",
    countColor: "#ad4e00",
    shadow: "0 4px 12px rgba(250,140,22,0.18)",
  },
  red: {
    background: "linear-gradient(135deg, #fff1f0 0%, #ffa39e 100%)",
    border: "2px solid #cf1322",
    accent: "#cf1322",
    iconColor: "#cf1322",
    statusColor: "#a8071a",
    countColor: "#a8071a",
    shadow: "0 6px 18px rgba(207,19,34,0.35)",
  },
  grey: {
    background: "linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)",
    border: "1px solid #d9d9d9",
    accent: "#8c8c8c",
    iconColor: "#8c8c8c",
    statusColor: "#595959",
    countColor: "#434343",
    shadow: "0 4px 12px rgba(0,0,0,0.06)",
  },
};

/** Policy doc cards (except GeneratedNotSent) go green/red; RecentlyLapsed stays grey; rest green/yellow. */
const getCardTone = (key: ComplianceCardKey, count: number): CardTone => {
  if (key === "RecentlyLapsed") return "grey";

  const isPolicyCritical = [
    "AnyMissingDocs",
    "ZeroDocuments",
    "IdDocument",
    "ApplicationDocument",
    "PolicyDocument",
    "InvalidCellphone",
  ].includes(key);

  if (count <= 0) return "green";
  return isPolicyCritical ? "red" : "yellow";
};

const CARD_ICONS: Record<ComplianceCardKey, React.ReactNode> = {
  IdDocument: <IdcardOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  ApplicationDocument: <FormOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  PolicyDocument: <FileProtectOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  AnyMissingDocs: <WarningOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  ZeroDocuments: <FileExcelOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  GeneratedNotSent: <MailOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  NoPaymentMethod: <WalletOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  DebiCheckIncomplete: <BankOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  DigitalOnboardingStuck: <MobileOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  LapseRisk: <ClockCircleOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  RecentlyLapsed: <StopOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  MissingBeneficiary: <UserDeleteOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  IncompleteCoveredLives: <TeamOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  ClaimsMissingDocs: <FileSearchOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  InvalidCellphone: <MobileOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  DeductionBelowPremium: <DollarOutlined style={{ fontSize: 16, marginRight: 6 }} />,
  PendingLapse: <ToolOutlined style={{ fontSize: 16, marginRight: 6 }} />,
};

const CRITICAL_CARD_KEYS: ComplianceCardKey[] = [
  "AnyMissingDocs",
  "ZeroDocuments",
  "IdDocument",
  "ApplicationDocument",
  "PolicyDocument",
  "InvalidCellphone",
];

const SECTIONS: { title: string; keys: ComplianceCardKey[] }[] = [
  {
    title: "Policy Documents",
    keys: [
      "AnyMissingDocs",
      "ZeroDocuments",
      "IdDocument",
      "ApplicationDocument",
      "PolicyDocument",
      "GeneratedNotSent",
    ],
  },
  {
    title: "Payments & DebiCheck",
    keys: [
      "NoPaymentMethod",
      "DebiCheckIncomplete",
      "PendingLapse",
      "LapseRisk",
      "RecentlyLapsed",
      "DeductionBelowPremium",
    ],
  },
  {
    title: "Member Data & Onboarding",
    keys: [
      "DigitalOnboardingStuck",
      "MissingBeneficiary",
      "IncompleteCoveredLives",
      "InvalidCellphone",
    ],
  },
  {
    title: "Claims",
    keys: ["ClaimsMissingDocs"],
  },
];

const CompliancePage: React.FC = () => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openedCardFromQuery = useRef<string | null>(null);

  const [compliance, setCompliance] = useState<DocumentComplianceData | null>(null);
  const [selected, setSelected] = useState<ComplianceCard | null>(null);
  const [show, setShow] = useState(false);
  const [isActive, setActive] = useState(true);
  const [productFilter, setProductFilter] = useState<number | null>(null);
  const [complianceRevision, setComplianceRevision] = useState(0);
  const selectedKeyRef = useRef<ComplianceCardKey | null>(null);
  const showToastOnLoadRef = useRef(false);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const role = currentUser?.Role?.Name;
  const isAdminOrAbove = role === "Admin" || role === "Super Admin";
  const isSuperAdmin = role === "Super Admin";
  const { pendingRuns, pendingCount } = usePendingLapseRuns(isSuperAdmin);

  const loadCompliance = useCallback((options?: { showToast?: boolean }) => {
    if (!currentGroup) return;

    if (options?.showToast) {
      showToastOnLoadRef.current = true;
    }

    setActive(true);
    Promise.all([
      getDocumentComplianceApi(String(currentGroup), { cacheBust: true }),
      getCompanyMembersApi(String(currentGroup)).catch(() => null),
    ])
      .then(([complianceRes, membersRes]) => {
        const data: DocumentComplianceData = complianceRes.data;
        const cards = Array.isArray(data?.Documents) ? [...data.Documents] : [];
        const members = Array.isArray(membersRes?.data) ? membersRes.data : [];

        if (!cards.some((card) => card.Key === "InvalidCellphone")) {
          cards.push(buildInvalidCellphoneCard(members));
        }
        if (!cards.some((card) => card.Key === "DeductionBelowPremium")) {
          cards.push(buildDeductionBelowPremiumCard(members));
        }

        // Prefer native BE cards (InvalidCellphone / DeductionBelowPremium).
        // Only synthesize when the API omits them (fallback for older responses).
        const enriched = cards.map((card) => {
          if (card.Key === "LapseRisk") return enrichLapseRiskCard(card, members);
          if (card.Key === "DebiCheckIncomplete")
            return enrichDebiCheckIncompleteCard(card, members);
          if (card.Key === "GeneratedNotSent")
            return enrichGeneratedNotSentCard(card, members);
          return card;
        });

        setCompliance({ ...data, Documents: enriched });
        setComplianceRevision((n) => n + 1);
        setActive(false);
        if (showToastOnLoadRef.current) {
          showToastOnLoadRef.current = false;
          message.success("Compliance refreshed.");
        }
      })
      .catch((err) => {
        console.log(err);
        showToastOnLoadRef.current = false;
        message.error("Unable to load compliance dashboard.");
        setActive(false);
      });
  }, [currentGroup]);

  useEffect(() => {
    if (role && !isAdminOrAbove) navigate("/dashboard");
  }, [role, isAdminOrAbove, navigate]);

  useEffect(() => {
    loadCompliance();
  }, [loadCompliance]);

  useEffect(() => {
    return subscribeComplianceInvalidate(() => {
      loadCompliance();
    });
  }, [loadCompliance]);

  const showModal = () => {
    setShow((prev) => {
      const next = !prev;
      if (!next) {
        selectedKeyRef.current = null;
      }
      return next;
    });
  };

  const filteredCards = useMemo(() => {
    const base = [...(compliance?.Documents ?? [])].filter(
      (card) => card.Key !== "PendingLapse"
    );
    if (isSuperAdmin) {
      base.push(buildPendingLapseCard(pendingRuns));
    }

    if (productFilter == null) return base;

    return base.map((card) => {
      if (card.Key === "PendingLapse") return card;
      const members = card.Members.filter((row) => row.ProductID === productFilter);
      return {
        ...card,
        Members: members,
        MissingCount: members.length,
      };
    });
  }, [compliance, productFilter, pendingRuns, isSuperAdmin]);

  // Keep open drilldown in sync with refreshed card data
  useEffect(() => {
    if (!show || !selectedKeyRef.current) return;
    const key = selectedKeyRef.current;
    const next = filteredCards.find((card) => card.Key === key) ?? null;
    setSelected(next);
    if (!next) {
      setShow(false);
      selectedKeyRef.current = null;
    }
  }, [filteredCards, complianceRevision, show]);

  const pendingLapseMenuItems: MenuProps["items"] = useMemo(
    () => [
      ...pendingRuns.map((run) => {
        const summary = formatPendingLapseSummary(run);
        return {
          key: String(run.id),
          label: (
            <div>
              <div>{summary.title}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {summary.description}
              </Typography.Text>
            </div>
          ),
          onClick: () => navigate(`/maintenance/${run.id}`),
        };
      }),
      { type: "divider" as const },
      {
        key: "open-maintenance",
        label: "Open Maintenance",
        onClick: () => navigate("/maintenance"),
      },
    ],
    [pendingRuns, navigate]
  );

  const cardByKey = useMemo(() => {
    const map = new Map<ComplianceCardKey, ComplianceCard>();
    filteredCards.forEach((card) => map.set(card.Key, card));
    return map;
  }, [filteredCards]);

  const criticalOutstanding = useMemo(
    () =>
      CRITICAL_CARD_KEYS.reduce(
        (sum, key) => sum + (cardByKey.get(key)?.MissingCount ?? 0),
        0
      ),
    [cardByKey]
  );

  const handleCardClick = (card: ComplianceCard) => {
    selectedKeyRef.current = card.Key;
    setSelected(card);
    setShow(true);
  };

  // App-bar drilldown: /compliance?card=InvalidCellphone opens that card modal
  useEffect(() => {
    const cardKey = searchParams.get("card");
    if (!cardKey || isActive) return;
    if (openedCardFromQuery.current === cardKey && show) return;

    const card = cardByKey.get(cardKey as ComplianceCardKey);
    if (!card) return;

    openedCardFromQuery.current = cardKey;
    selectedKeyRef.current = card.Key;
    setSelected(card);
    setShow(true);
    const next = new URLSearchParams(searchParams);
    next.delete("card");
    setSearchParams(next, { replace: true });
  }, [searchParams, cardByKey, isActive, show, setSearchParams]);

  const activeMembers = compliance?.ActiveMembers ?? 0;
  const totalMembers = compliance?.TotalMembers ?? 0;

  const renderCard = (key: ComplianceCardKey) => {
    const card = cardByKey.get(key);
    if (!card) return null;

    const tone = getCardTone(key, card.MissingCount);
    const toneStyle = TONE_STYLES[tone];
    const isRed = tone === "red";

    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={key}>
        <Card
          hoverable
          size="small"
          onClick={() => handleCardClick(card)}
          bodyStyle={{ padding: "10px 12px" }}
          style={{
            border: toneStyle.border,
            borderLeft: `4px solid ${toneStyle.accent}`,
            borderRadius: 8,
            boxShadow: toneStyle.shadow,
            background: toneStyle.background,
            height: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
            <span style={{ color: toneStyle.iconColor, display: "inline-flex" }}>
              {CARD_ICONS[key]}
            </span>
            <Text
              style={{
                fontSize: 12,
                fontWeight: isRed ? 700 : 600,
                color: isRed ? "#a8071a" : "#595959",
                lineHeight: 1.2,
              }}
            >
              {card.Label}
            </Text>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span
              style={{
                fontSize: isRed ? 22 : 20,
                fontWeight: 700,
                lineHeight: 1.1,
                color: toneStyle.countColor,
              }}
            >
              {card.MissingCount}
            </span>
            <Text
              style={{
                fontSize: 11,
                fontWeight: isRed ? 700 : 500,
                textTransform: isRed ? "uppercase" : "none",
                color: toneStyle.statusColor,
              }}
            >
              {card.MissingCount > 0
                ? tone === "grey"
                  ? "lapsed"
                  : key === "PendingLapse"
                    ? "awaiting"
                    : "outstanding"
                : "clear"}
            </Text>
          </div>
          <Text
            style={{
              fontSize: 11,
              display: "block",
              marginTop: 4,
              color: "#8c8c8c",
              lineHeight: 1.3,
            }}
          >
            {card.Description}
          </Text>
        </Card>
      </Col>
    );
  };

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h2 style={{ margin: 0 }}>
                    <SafetyCertificateOutlined
                      style={{ paddingRight: "5px", color: "#f04f05" }}
                    />
                    Compliance
                  </h2>
                  <ReloadOutlined
                    onClick={() => {
                      if (isActive) return;
                      loadCompliance({ showToast: true });
                    }}
                    spin={isActive}
                    title="Refresh"
                    style={{
                      fontSize: 18,
                      color: "#f04f05",
                      stroke: "#f04f05",
                      strokeWidth: 40,
                      cursor: isActive ? "not-allowed" : "pointer",
                      opacity: isActive ? 0.6 : 1,
                    }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <Select
                    allowClear
                    placeholder="Filter by product"
                    style={{ minWidth: 240 }}
                    value={productFilter ?? undefined}
                    onChange={(value) => setProductFilter(value ?? null)}
                    options={(compliance?.Products ?? []).map((product) => ({
                      value: product.id,
                      label: product.name,
                    }))}
                  />
                  {isSuperAdmin && pendingCount > 0 && (
                    <Dropdown menu={{ items: pendingLapseMenuItems }} trigger={["click"]}>
                      <Button type="primary" danger icon={<ToolOutlined />}>
                        Pending lapse ({pendingCount}) <DownOutlined />
                      </Button>
                    </Dropdown>
                  )}
                </div>
              </div>
              <Text type="secondary">
                Compliance checks across {activeMembers} active member
                {activeMembers === 1 ? "" : "s"}
                {totalMembers !== activeMembers
                  ? ` (${totalMembers} total including inactive/lapsed)`
                  : ""}
                . Click a card to drill down. Age is days since inception (or lapse/claim date). At Risk Of Lapsing shows months behind (outstanding ÷ premium).
              </Text>

              {isSuperAdmin && pendingCount > 0 && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  icon={<ToolOutlined />}
                  message={`${pendingCount} pending lapse run${
                    pendingCount === 1 ? "" : "s"
                  } awaiting approval`}
                  description={
                    <div>
                      <Typography.Paragraph style={{ marginBottom: 8 }}>
                        Review and approve or reject in Maintenance before policies
                        are lapsed and SMS is sent.
                      </Typography.Paragraph>
                      <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                        {pendingRuns.slice(0, 5).map((run) => {
                          const summary = formatPendingLapseSummary(run);
                          return (
                            <li key={run.id}>
                              <Typography.Link
                                onClick={() => navigate(`/maintenance/${run.id}`)}
                              >
                                {summary.title}
                              </Typography.Link>
                              <Typography.Text type="secondary" style={{ marginLeft: 6 }}>
                                {summary.description}
                              </Typography.Text>
                            </li>
                          );
                        })}
                        {pendingRuns.length > 5 && (
                          <li>
                            <Typography.Text type="secondary">
                              +{pendingRuns.length - 5} more
                            </Typography.Text>
                          </li>
                        )}
                      </ul>
                      <Typography.Link onClick={() => navigate("/maintenance")}>
                        Open Maintenance
                      </Typography.Link>
                    </div>
                  }
                />
              )}

              {compliance?.Truncated && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message={`Document checks only scanned the first ${compliance.ScannedMembers} of ${activeMembers} active members.`}
                />
              )}

              {criticalOutstanding > 0 && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="error"
                  showIcon
                  message={`${criticalOutstanding} member${
                    criticalOutstanding === 1 ? "" : "s"
                  } with missing critical policy documents`}
                  description="Review the Policy Documents cards below and click a card to drill down."
                />
              )}

              {!!compliance?.FailedMembers && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message={`${compliance.FailedMembers} member${
                    compliance.FailedMembers === 1 ? "" : "s"
                  } could not be document-scanned and are excluded from document counts.`}
                />
              )}

              {SECTIONS.map((section) => (
                <div key={section.title} style={{ marginTop: 20 }}>
                  <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>
                    {section.title}
                  </Text>
                  <Row gutter={[12, 12]}>{section.keys.map(renderCard)}</Row>
                </div>
              ))}

              {selected && (
                <NonCompliantMembersModal
                  key={`${selected.Key}-${productFilter ?? "all"}-${complianceRevision}`}
                  show={show}
                  showModal={showModal}
                  cardKey={selected.Key}
                  title={selected.Label}
                  members={selected.Members}
                />
              )}
            </div>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default CompliancePage;
